/* eslint-disable max-lines */
import React, { useEffect, useState, useRef, useCallback, useMemo, useReducer } from 'react';
import { Spin, Typography, Select, Divider, Tooltip, Switch, Icon } from '@fuxi/eevee-ui';
import { useKeyPress } from 'ahooks';
import { debounce, isNil } from 'lodash-es';
import EeveeIcon from 'common/components/EeveeIcon';
import { ExportFileType, ExportVersionFile } from 'common/types';
import { useTaskEditorContext } from '@/context/TaskEditorContext';
import { CommentAssetPosition } from '@/types';
import loadingDarkImg from '@/assets/image/loading-dark.png';
import PlayRateDropdown from './PlayRateDropdown';
import { fillZero, calculateTime, getNumberOfDigits } from './util';

import Volume from './Volume';
import ReviewMarkTip from './ReviewMarkTip';

import cx from './index.module.css';
import PlayListMark from './PlayListMark';

const tipAfterHeight = 12;
let tickWidth = 0;

export const defaultStyle = {
  splitCount: 20,
  tickHeight: 20,
  tickColorBgOdd: 'rgba(21, 24, 36, 1)',
  tickColorBgEven: 'rgba(34, 38, 56, 1)',
  tipWidth: 50,
  tipHeight: 20,
  tipFontSize: 12,
  tipColor: 'rgba(255, 255, 255, 1)',
  tipBg: 'var(--color-primary)',
  tailColor: 'rgba(255, 255, 255, 1)',
  tailBgColor: '#16213E',
  tailFontSize: 14,
  markMiddleColorBg: 'var(--color-primary)',
  markMiddleWidth: 2,
};

export enum PlayerBarShowCell {
  Time = 'time',
  Frame = 'frame',
}

export enum PlayerBarActionMode {
  Review = 'review',
  Hover = 'hover',
  Down = 'down',
  Move = 'move',
  ShortCutReview = 'shortCutReview',
  Other = '',
}

interface PlayerBarProps {
  playerRef: any;
  type: ExportFileType;
  dataSources: any[];
  onPlay: () => void;
  onPause: () => void;
  onSeek: (frameIndex: number, mode: PlayerBarActionMode) => void;
  defaultTakeIndex?: number;
  totalFrames?: number;
  reviewList?: CommentAssetPosition[];
  isPause: boolean;
  setIsPause: (bool: boolean) => void;
  isLoop: boolean;
  setIsLoop: (bool: boolean) => void;
  loading: boolean;
  setLoading: (bool: boolean) => void;
  frameIndex: number;
  curTakeIndex: number;
  setCurTakeIndex: (index: number) => void;
  isKeyEventActive: boolean;
  frameRate: number;
  setFrameRate: (rate: number) => void;
  originalFrameRate: number;
  isEventDisabled: boolean;
  setFileLoading: (bool: boolean) => void;
  onFramesPerPxChange: (width: number) => void;
  playList: ExportVersionFile[];
}
const markStyle = ({ markLeft }) => {
  const style = {
    width: `${defaultStyle.markMiddleWidth}px`,
    left: `${markLeft}px`,
  };
  return style;
};
const calculateMarkLeft = ({ tickWidth, markMiddleWidth, frameIndex, mode }) => {
  let markLeft = (frameIndex - 1) * tickWidth + tickWidth / 2 - markMiddleWidth / 2; // 向右偏移半格,减去中线半宽，居中对齐
  if (mode === 'review') {
    markLeft -= tickWidth / 2;
  }

  return markLeft;
};

const tipStyle = ({ isPause }) => {
  const { markMiddleWidth, tipFontSize, tipColor, tipBg } = defaultStyle;
  return {
    transform: `translate(calc(-50% + ${markMiddleWidth / 2}px), calc(-100% - ${tipAfterHeight}px))`,
    fontSize: `${tipFontSize}px`,
    color: `${tipColor}`,
    backgroundColor: `${tipBg}`,
    display: isPause ? 'flex' : 'none',
  };
};
const initialToolTip = { prevComment: false, nextComment: false, loop: false };
const PlayerBar: React.FC<PlayerBarProps> = ({
  totalFrames,
  defaultTakeIndex,
  curTakeIndex,
  frameIndex,
  frameRate,
  isLoop,
  setIsPause,
  onPlay,
  onPause,
  onSeek,
  setIsLoop,
  onTogglePlayList,
  playerRef,
  type,
  isPause,
  playList,
  isKeyEventActive,
  setFileLoading,
  dataSources,
  setFrameRate,
  reviewList,
  loading,
  originalFrameRate,
  setCurTakeIndex,
  onFramesPerPxChange,
}) => {
  const { saveDefaultComment, setIsPlayList, isPlayList } = useTaskEditorContext();

  const [curTickWidth, setCurTickWidth] = useState(0);
  const [frameTotal, setFrameTotal] = useState(totalFrames || 0);
  const [frameTotalDigits, setFrameTotalDigits] = useState(0);
  const [barWidth, setBarWidth] = useState(1728);

  const [markHoverLeft, setMarkHoverLeft] = useState(60);
  const [markLeft, setMarkLeft] = useState(
    calculateMarkLeft({
      tickWidth: tickWidth,
      markMiddleWidth: defaultStyle.markMiddleWidth,
      frameIndex: frameIndex,
      mode: 'normal',
    }),
  );
  const [isMouseHover, setIsMouseHover] = useState(false);

  const playerBarRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const markRef = useRef<HTMLDivElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const isMouseDown = useRef(false);
  const draggedRef = useRef(false);
  const spaceDownRef = useRef(false);

  // 播放器相关状态
  const [volume, setVolume] = useState(30);
  const [lastVolume, setLastVolume] = useState(0);
  const [isSoundOff, setIsSoundOff] = useState(false);

  const [showCell, setShowCell] = useState(PlayerBarShowCell.Frame);

  function toolTipsReducer(tooltips, action) {
    return { ...initialToolTip, [action.type]: action.open };
  }
  const [controlToolTip, dispatchControlToolTip] = useReducer(toolTipsReducer, initialToolTip);

  const onPlayListModeChange = enable => {
    onTogglePlayList?.(enable);
  };

  const handlePlay = useCallback(() => {
    setIsPause(false);
    onPlay();
  }, [onPlay]);
  const handlePause = useCallback(() => {
    setIsPause(true);
    onPause();
  }, [onPause]);

  useEffect(() => {
    setFrameTotal(totalFrames ?? 0);
  }, [totalFrames]);

  useEffect(() => {
    tickWidth = barWidth / frameTotal;
    setCurTickWidth(tickWidth);
    onFramesPerPxChange(frameTotal / barWidth);
    return () => {
      tickWidth = 0;
      setCurTickWidth(0);
      onFramesPerPxChange(0);
    };
  }, [frameTotal, barWidth, defaultTakeIndex, curTakeIndex]);

  const handlePrev = async () => {
    if (frameIndex <= 1) return;

    const sub = type === 'animation' ? -2 : -1;

    if (isPause) {
      await onSeek(frameIndex + sub, PlayerBarActionMode.Other);
    }
    playerRef?.current?.goToFrame(frameIndex + sub);
  };

  const handleNext = async () => {
    if (frameIndex >= frameTotal) return;

    const add = type === 'animation' ? 0 : 1;

    if (isPause) {
      await onSeek(frameIndex + add, PlayerBarActionMode.Other);
    }
    playerRef?.current?.goToFrame(frameIndex + add);
  };
  const resizeBarWidth = () => setBarWidth(contentRef?.current?.offsetWidth as number);

  useKeyPress(
    'leftArrow',
    event => {
      event.preventDefault();
      isKeyEventActive && handlePrev();
    },
    { events: ['keyup'], target: window },
  );
  useKeyPress(
    'rightArrow',
    event => {
      event.preventDefault();
      isKeyEventActive && handleNext();
    },
    { events: ['keyup'], target: window },
  );

  useKeyPress(
    'period',
    event => {
      event.preventDefault();
      if (isKeyEventActive) {
        handleNextCommentFrame();
      }
    },
    {
      events: ['keyup'],
      target: window,
    },
  );

  useKeyPress(
    'comma',
    event => {
      event.preventDefault();
      if (isKeyEventActive) {
        handlePrevCommentFrame();
      }
    },
    {
      events: ['keyup'],
      target: window,
    },
  );
  useKeyPress(
    'space',
    event => {
      event.stopPropagation();
      event.preventDefault();
      if (event.type === 'keydown' && !spaceDownRef.current) {
        draggedRef.current = false;
        spaceDownRef.current = true;
      } else if (event.type === 'keyup') {
        spaceDownRef.current = false;
        if (!draggedRef.current && isKeyEventActive) {
          isPause ? handlePlay() : handlePause();
        }
      }
    },
    { events: ['keydown', 'keyup'], target: window },
  );

  const handleNextCommentFrame = debounce(
    async () => {
      if (type !== 'video') {
        return;
      }

      console.log('handleNextCommentFrame');
      const index = Math.round(frameIndex);
      //从当前帧，查找下一个有反馈的帧
      const nextComment = reviewList?.find(item => item.frameIndex > index);
      console.log('handleNextCommentFrame', reviewList, nextComment);
      if (nextComment) {
        await onSeek(nextComment.frameIndex, PlayerBarActionMode.ShortCutReview);
        // playerRef?.current?.goToFrame(nextComment.frameIndex);
      } else {
        //如果是循环播放，且没有下一个反馈帧，则跳到第一个反馈帧
        if (isLoop && reviewList && reviewList?.length > 0) {
          console.log('handleNextCommentFrame', reviewList[0].frameIndex);
          await onSeek(reviewList[0].frameIndex, PlayerBarActionMode.ShortCutReview);
          // playerRef?.current?.goToFrame(reviewList[0].frameIndex);
        }
      }
    },
    200,
    {
      leading: true,
      trailing: false,
    },
  );
  const handlePrevCommentFrame = debounce(
    async () => {
      if (type !== 'video') {
        return;
      }
      const index = Math.round(frameIndex);
      //从当前帧，查找上一个有反馈的帧
      const prevComment = reviewList?.findLast(item => item.frameIndex < index);
      if (prevComment) {
        await onSeek(prevComment.frameIndex, PlayerBarActionMode.ShortCutReview);
        // playerRef?.current?.goToFrame(prevComment.frameIndex);
      } else {
        //如果是循环播放，且没有上一个反馈帧，则跳到最后一个反馈帧
        if (isLoop && reviewList && reviewList?.length > 0) {
          await onSeek(reviewList[reviewList.length - 1].frameIndex, PlayerBarActionMode.ShortCutReview);
          // playerRef?.current?.goToFrame(reviewList[reviewList.length - 1].frameIndex);
        }
      }
    },
    200,
    {
      leading: true,
      trailing: false,
    },
  );

  const handleMouseEventForMark = useCallback(
    async (config: any) => {
      const { e, mode } = config;
      const { clientX: cursorLeft } = e;
      const { clientLeft: contentLeft, offsetWidth: contentWidth } = contentRef?.current as HTMLDivElement;
      const isOverflowLeft = cursorLeft < contentLeft;
      const isOverflowRight = cursorLeft > contentLeft + contentWidth;

      // 边界处理
      switch (mode) {
        case PlayerBarActionMode.Move:
        case PlayerBarActionMode.Hover:
          if (isOverflowLeft || isOverflowRight) {
            return;
          }
          break;
        case PlayerBarActionMode.Down:
          break;
      }
      let offsetLeft = cursorLeft - contentLeft;
      let offsetCount = Math.ceil(offsetLeft / tickWidth) - 1;

      // curFrameIndex需要取整
      let curFrameIndex = Math.ceil(offsetCount) + (type === 'video' ? 1 : 0);
      if (isOverflowLeft) {
        curFrameIndex = type === 'video' ? 1 : 0;
      }
      if (isOverflowRight) {
        curFrameIndex = type === 'video' ? frameTotal : frameTotal - 1;
      }
      if (mode !== PlayerBarActionMode.Hover) {
        if (type === 'video') {
          const video = playerRef?.current?.getElement();
          if (video) {
            const curTime = curFrameIndex / frameRate;
            // 判断当前的帧对应的时间，是否在视频已缓冲的区间内
            const timeRanged = video.buffered;
            let isTimeRanged = false;
            for (let i = 0; i < timeRanged.length; i++) {
              if (curTime >= timeRanged.start(i) && curTime <= timeRanged.end(i)) {
                isTimeRanged = true;
                break;
              }
            }
            if (!isTimeRanged) {
              // setFileLoading(true); // 暂时移除拖动Loading，体验不好
            }
          }
        }
        if (mode === PlayerBarActionMode.Down) {
          console.log('handlePause');
          handlePause();
          await onSeek(curFrameIndex, mode);
        }

        playerRef?.current?.goToFrame(curFrameIndex);
      }

      mode === 'hover' && setMarkHoverLeft(offsetCount * tickWidth);
    },
    [
      frameTotal,
      type,
      contentRef?.current,
      playerRef.current,
      onSeek,
      setMarkLeft,
      setMarkHoverLeft,
      frameRate,
      isPause,
    ],
  );
  const handleMouseLeave = useCallback(() => {
    setIsMouseHover(false);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsMouseHover(false);
    isMouseDown.current = false;
    draggedRef.current = true;
    document.documentElement.style.setProperty('cursor', 'auto');
  }, [isMouseDown.current, draggedRef.current, isMouseHover, reviewList]);

  const handleMouseDown = async (e: any) => {
    isMouseDown.current = true;

    await saveDefaultComment();
    handleMouseEventForMark({ e, mode: PlayerBarActionMode.Down });
    document.documentElement.style.setProperty('cursor', 'pointer');
  };

  const handleContentMouseMove = useCallback(
    (e: any) => {
      if (isMouseDown.current) {
        setIsMouseHover(false);
        return;
      }

      if (!isMouseHover) setIsMouseHover(true);

      handleMouseEventForMark({ e, mode: PlayerBarActionMode.Hover });
    },
    [isMouseHover, isMouseDown.current],
  );

  const handleMouseMove = useCallback(
    (e: any) => {
      if (!isMouseDown.current) return;
      handleMouseEventForMark({ e, mode: PlayerBarActionMode.Move });
    },
    [isMouseDown.current],
  );

  const handleToggleCell = useCallback(() => {
    const { Frame, Time } = PlayerBarShowCell;
    setShowCell(prev => {
      return prev === Frame ? Time : Frame;
    });
  }, []);

  const handleToggleLoop = useCallback(() => {
    if (!isPause) {
      playerRef?.current?.pause(dataSources[curTakeIndex].name);
      playerRef?.current?.play(!isLoop, dataSources[curTakeIndex].name);
    }
    setIsLoop(!isLoop);
  }, [isLoop, dataSources, curTakeIndex, isPause]);

  const handleSoundClick = useCallback(() => {
    if (!isSoundOff) {
      setLastVolume(volume);
      setVolume(0);
    } else {
      setVolume(lastVolume);
    }
    setIsSoundOff(prev => {
      playerRef?.current?.setVolume?.(prev ? lastVolume : 0);
      return !prev;
    });
  }, []);

  const handleSoundChange = useCallback(val => {
    if (isSoundOff) setIsSoundOff(false);

    setVolume(val);
  }, []);

  const handleFrameRateChange = useCallback((frameRate, speedRatio) => {
    setFrameRate(frameRate);
    playerRef?.current?.setSpeedRatio(speedRatio);
  }, []);

  useEffect(() => {
    resizeBarWidth();
    window.addEventListener('resize', resizeBarWidth);

    document.documentElement.addEventListener('mousemove', handleMouseMove);
    document.documentElement.addEventListener('mouseup', handleMouseUp);
    const content = contentRef.current;

    if (content) {
      content.addEventListener('mousedown', handleMouseDown);
      content.addEventListener('mouseup', handleMouseUp);
      content.addEventListener('mouseleave', handleMouseLeave);
      content.addEventListener('mousemove', handleContentMouseMove);
    }

    return () => {
      window.removeEventListener('resize', resizeBarWidth);
      document.documentElement.removeEventListener('mousemove', handleMouseMove);
      document.documentElement.removeEventListener('mouseup', handleMouseUp);
      if (contentRef.current) {
        contentRef.current.removeEventListener('mousedown', handleMouseDown);
        contentRef.current.removeEventListener('mouseup', handleMouseUp);
        contentRef.current.removeEventListener('mouseleave', handleMouseLeave);
        contentRef.current.removeEventListener('mousemove', handleContentMouseMove);
      }
    };
  }, [type, reviewList]);
  useEffect(() => {
    playerRef?.current?.setVolume?.(volume);
    setIsSoundOff(volume === 0 ? true : false);
  }, [volume]);

  useEffect(() => {
    if (dataSources.length > 0) {
      const animation = dataSources[curTakeIndex];

      if (animation && type === 'animation') {
        setFrameTotal(animation.frames);
        setFrameTotalDigits(getNumberOfDigits(animation.frames));
      }
    }
  }, [dataSources, curTakeIndex, type]);

  useEffect(() => {
    // if (isMouseDown.current) return;
    const markLeft = calculateMarkLeft({
      tickWidth: tickWidth,
      markMiddleWidth: defaultStyle.markMiddleWidth,
      frameIndex: frameIndex,
      mode: 'normal',
    });

    setMarkLeft(markLeft);

    if (!isNil(totalFrames) && frameIndex >= totalFrames && !isLoop) {
      setIsPause(true);
    }
  }, [frameIndex, totalFrames, setIsPause, isPause, isLoop]);

  const markHoverStyle = ({ markHoverLeft = 50, tickWidth, isMouseHover, frameIndex, mode }) => {
    const left =
      mode === PlayerBarActionMode.Review
        ? calculateMarkLeft({ frameIndex, mode, tickWidth, markMiddleWidth: defaultStyle.markMiddleWidth })
        : markHoverLeft;
    const opacity = mode === PlayerBarActionMode.Review || isMouseHover ? 1 : 0;
    const width = tickWidth < defaultStyle.markMiddleWidth ? defaultStyle.markMiddleWidth : tickWidth;

    return {
      width: `${width}px`,
      left: `${left}px`,
      opacity,
    };
  };
  const renderMarkDom = () => {
    return (
      <>
        <div className={cx('mark')} ref={markRef} style={markStyle({ markLeft })}>
          <div
            className={cx('mark-middle')}
            style={{
              width: `${defaultStyle.markMiddleWidth}px`,
              backgroundColor: defaultStyle.markMiddleColorBg,
            }}>
            <div className={cx('mark-tip')} ref={tipRef} style={tipStyle({ isPause: isPause })}>
              {/** 兼容旧的有小数的frameIndex记录 */}
              {Math.round(frameIndex)}
              <div className={cx('mark-tip-after')} style={{ background: defaultStyle.tipBg }}></div>
            </div>
          </div>
        </div>
        <div
          className={cx('mark', 'mark-hover')}
          ref={markRef}
          style={markHoverStyle({
            frameIndex: frameIndex,
            mode: PlayerBarActionMode.Hover,
            tickWidth: tickWidth,
            markHoverLeft,
            isMouseHover: isMouseHover,
          })}>
          <div
            className={cx('mark-middle')}
            style={{
              width: `${defaultStyle.markMiddleWidth}px`,
              backgroundColor: defaultStyle.markMiddleColorBg,
            }}
          />
        </div>
      </>
    );
  };

  const shouldDisabledNextIcon = useMemo(() => {
    if (isLoop) {
      return '';
    } else {
      return reviewList &&
        reviewList?.length > 0 &&
        Math.round(frameIndex) >= reviewList?.[reviewList?.length - 1].frameIndex
        ? 'control-icon--disabled'
        : '';
    }
  }, [reviewList, isLoop, frameIndex]);

  const shouldDisabledPrevIcon = useMemo(() => {
    if (isLoop) {
      return '';
    } else {
      return reviewList && Math.round(frameIndex) <= reviewList?.[0]?.frameIndex ? 'control-icon--disabled' : '';
    }
  }, [reviewList, isLoop, frameIndex]);

  return (
    <div
      ref={playerBarRef}
      className={cx('player-bar', {
        playlist: isPlayList,
      })}>
      <Spin
        wrapperClassName={cx('spin')}
        size="small"
        spinning={loading}
        indicator={<img src={loadingDarkImg} className={cx('spinner')} alt="loading" />}
        tip={<Typography.Text>加载中...</Typography.Text>}>
        {type === 'video' && <div className={cx('player-bar-progress')} id="player-bar-progress"></div>}
        <div className={cx('show')}>
          <div
            className={cx('content')}
            ref={contentRef}
            style={{
              height: '22px',
              width: `95%`,
              backgroundImage: `repeating-linear-gradient(
                    to right,
                    ${defaultStyle.tickColorBgOdd} 0,
                    ${defaultStyle.tickColorBgOdd} ${tickWidth}px,
                    ${defaultStyle.tickColorBgEven} ${tickWidth}px,
                    ${defaultStyle.tickColorBgEven} ${tickWidth * 2}px
                  )`,
            }}>
            <ReviewMarkTip reviewList={reviewList} curTakeIndex={curTakeIndex} tickWidth={curTickWidth} />

            {isPlayList && (
              <PlayListMark playList={playList} curTakeIndex={curTakeIndex} tickWidth={curTickWidth}></PlayListMark>
            )}

            {renderMarkDom()}
          </div>
          <div
            className={cx('tail')}
            style={{
              color: defaultStyle.tailColor,
              backgroundColor: defaultStyle.tailBgColor,
              fontSize: `${defaultStyle.tailFontSize}px`,
              width: '5%',
            }}>
            {frameTotal}
          </div>
        </div>
        <div className={cx('control')}>
          <div className={cx('left-part')}>
            {isPause ? (
              <EeveeIcon onClick={handlePlay} icon="play_93Rvat" size={24} className={cx('control-icon')} />
            ) : (
              <EeveeIcon onClick={handlePause} icon="pause_ARvaCA" size={24} className={cx('control-icon')} />
            )}
            <Tooltip open={controlToolTip.loop} title={isLoop ? '循环播放' : '单次播放'}>
              <EeveeIcon
                onMouseEnter={() => dispatchControlToolTip({ type: 'loop', open: true })}
                onMouseLeave={() => dispatchControlToolTip({ type: 'loop', open: false })}
                onClick={() => {
                  dispatchControlToolTip({ type: 'loop', open: false });
                  handleToggleLoop();
                }}
                icon={isLoop ? 'repeat-2-line' : 'order-play-line'}
                size={24}
                className={cx('control-icon')}
              />
            </Tooltip>
            <div className={cx('cell-wrap')} onClick={handleToggleCell}>
              <EeveeIcon icon="zhuanhuan-1" size={24} />
              {showCell === PlayerBarShowCell.Frame ? (
                <>
                  <span>{fillZero(Math.round(frameIndex), frameTotalDigits)}</span>/
                  <span className={cx('cell-frame-total')}>{frameTotal}</span>
                </>
              ) : (
                <span>{`${calculateTime({
                  frameIndex: frameIndex,
                  frameRate: frameRate,
                })}`}</span>
              )}
            </div>
            <div className={cx('select-wrap')}>
              <PlayRateDropdown
                onFrameRateChange={handleFrameRateChange}
                originalFrameRate={originalFrameRate}
                frameRate={frameRate}
              />
            </div>
            {type === 'video' && (
              <>
                <Tooltip
                  open={controlToolTip.prevComment}
                  title={
                    <div className={cx('tooltip-content')}>
                      上一反馈帧
                      <span className={cx('icon')}>
                        <Icon name="tiaodaoshangyizhen" />
                      </span>
                    </div>
                  }>
                  <EeveeIcon
                    onMouseEnter={() => dispatchControlToolTip({ type: 'prevComment', open: true })}
                    onMouseLeave={() => dispatchControlToolTip({ type: 'prevComment', open: false })}
                    onClick={() => {
                      dispatchControlToolTip({ type: 'prevComment', open: false });
                      handlePrevCommentFrame();
                    }}
                    icon="skip-backward-mini-fill"
                    size={24}
                    className={cx('control-icon', shouldDisabledPrevIcon)}
                  />
                </Tooltip>
                <Tooltip
                  open={controlToolTip.nextComment}
                  title={
                    <div className={cx('tooltip-content')}>
                      下一反馈帧
                      <span className={cx('icon')}>
                        <Icon name="tiaodaoxiayizhen" />
                      </span>
                    </div>
                  }>
                  <EeveeIcon
                    onMouseEnter={() => dispatchControlToolTip({ type: 'nextComment', open: true })}
                    onMouseLeave={() => dispatchControlToolTip({ type: 'nextComment', open: false })}
                    onClick={() => {
                      dispatchControlToolTip({ type: 'nextComment', open: false });
                      handleNextCommentFrame();
                    }}
                    icon="skip-forward-mini-fill"
                    size={24}
                    className={cx('control-icon', shouldDisabledNextIcon)}
                  />
                </Tooltip>
              </>
            )}

            {/** 修改预览动作片段  */}
            {type === 'animation' && (
              <>
                <Tooltip
                  title={
                    <div className={cx('tooltip-content')}>
                      上一帧
                      <span className={cx('icon')}>
                        <EeveeIcon icon="keyboard-left" />
                      </span>
                    </div>
                  }>
                  <EeveeIcon
                    onClick={handlePrev}
                    icon="page-first"
                    size={24}
                    className={cx(
                      'control-icon',
                      !isLoop && totalFrames && frameIndex >= totalFrames ? 'control-icon--disabled' : '',
                    )}
                  />
                </Tooltip>
                <Tooltip
                  title={
                    <div className={cx('tooltip-content')}>
                      下一帧
                      <span className={cx('icon')}>
                        <EeveeIcon icon="keyboard-right" />
                      </span>
                    </div>
                  }>
                  <EeveeIcon
                    onClick={handleNext}
                    icon="page-last"
                    size={24}
                    className={cx(
                      'control-icon',
                      !isLoop && totalFrames && frameIndex >= totalFrames ? 'control-icon--disabled' : '',
                    )}
                  />
                </Tooltip>
                <Divider type="vertical" className={cx('left-part-divider')} />
                <Select
                  popupClassName={cx('animation-select-popup')}
                  className={cx('animation-select')}
                  onChange={val => {
                    if (playerRef?.current) {
                      handlePause();

                      playerRef.current?.goToFrame(0);
                      playerRef.current?.selectAnimation(dataSources[val].name);
                    }
                    setCurTakeIndex(val);
                  }}
                  value={curTakeIndex}
                  defaultValue={curTakeIndex}
                  options={dataSources.map((source, index) => {
                    return { value: index, label: source.name, ...source };
                  })}
                  style={{ width: '140px' }}
                  suffixIcon={
                    <EeveeIcon icon="chevron-down" style={{ rotate: '180deg', color: 'var(--color-text)' }} />
                  }
                />
              </>
            )}
          </div>
          <div className={cx('right-part', 'gap-6')}>
            {type === 'video' && (
              // <div className={cx('sound-wrap')}>
              //   <MyIcon
              //     onClick={handleSoundClick}
              //     icon={isSoundOff ? 'sound-off' : 'sound'}
              //     size={20}
              //     className={cx('control-icon')}
              //   />
              //   <Slider
              //     className={cx('sound-slider', isSoundOff && 'sound-slider--off')}
              //     value={volume}
              //     onChange={handleSoundChange}
              //     disabled={false}
              //   />
              //   <span>{volume}</span>
              // </div>
              <>
                <div className="flex gap-2 items-center">
                  连播
                  <Switch value={isPlayList} onChange={onPlayListModeChange}></Switch>
                </div>
                <Volume
                  volume={volume}
                  isSoundOff={isSoundOff}
                  handleSoundChange={handleSoundChange}
                  handleSoundClick={handleSoundClick}
                />
              </>
            )}
          </div>
        </div>
      </Spin>
    </div>
  );
};

export default PlayerBar;
